import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isFunction } from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { DefaultButton, IButtonProps } from '@fluentui/react';

import { ConfirmDialog, ConfirmDialogProps } from './confirm-dialog';
import { useCreatePromise } from '../use-create-promise';

export interface SecondaryButtonProps extends Omit<IButtonProps, 'key' | 'content'> {
  key: string;
  content: React.ReactNode;
  dismissDialog?: (buttonKey: string) => void;
  shouldDismissDialog?: boolean;
}

interface InternalSecondaryButtonProps extends Omit<SecondaryButtonProps, 'key' | 'dismissDialog'> {
  buttonKey: string;
  dismissDialog: (buttonKey: string) => void;
}

const SecondaryButton = (props: InternalSecondaryButtonProps) => {
  const {
    buttonKey,
    className,
    content,
    dismissDialog,
    onClick: originalOnClick,
    shouldDismissDialog,
  } = props;

  const onClick = useCallback((...params: Parameters<NonNullable<IButtonProps['onClick']>>) => {
    if (shouldDismissDialog) {
      dismissDialog(buttonKey);
    }

    return originalOnClick?.(...params);
  }, [buttonKey, dismissDialog, originalOnClick, shouldDismissDialog]);

  return (
    <DefaultButton
      className={classNames(className, 'dialog-dismiss')}
      onClick={onClick}
    >
      {content}
    </DefaultButton>
  );
};

SecondaryButton.propTypes = {
  buttonKey: PropTypes.string.isRequired,
  content: PropTypes.node.isRequired,
  className: PropTypes.string,
  dismissDialog: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  shouldDismissDialog: PropTypes.bool,
};

SecondaryButton.defaultProps = {
  className: null,
  shouldDismissDialog: true,
};

export interface DialogActivity {
  trace: (message: string) => void;
}

export interface DialogInstrumentationInfo {
  activity?: DialogActivity;
  dialogName?: string;
}

export interface UseConfirmDialogProps extends Omit<
  ConfirmDialogProps,
  'hidden' | 'onConfirm' | 'onCancel' | 'secondaryButtons'
> {
  hidden?: boolean;
  content?: React.ReactNode;
  onConfirm: () => void;
  onCancel?: () => void;
  onDisplay?: () => void;
  secondaryButtons?: SecondaryButtonProps[];
}

export type ShowDialog = (periodProps?: Partial<UseConfirmDialogProps>) => void;
export type UseConfirmDialogResult = [React.ReactElement, ShowDialog, boolean];

export function useConfirmDialog(
  defaultProps: UseConfirmDialogProps,
  instrumentationInfo: DialogInstrumentationInfo = {}
): UseConfirmDialogResult {
  const [additionalProps, setAdditionalProps] = useState<Partial<UseConfirmDialogProps> & { hidden: boolean }>({ hidden: true });
  const {
    content,
    onConfirm,
    onCancel: originalOnCancel,

    // onDismiss is used to controll the top-right close button and light dismiss
    // if onDismiss parameter is not provided, keep same behavior as cancel button
    onDismiss: originalOnDismiss,

    secondaryButtons = [],
    onDisplay,
    hidden,
    ...passThroughProps
  }: UseConfirmDialogProps = {
    ...defaultProps,
    ...additionalProps,
  };

  const {
    activity,
    dialogName,
  } = instrumentationInfo;

  const showDialog = useCallback((periodProps: Partial<UseConfirmDialogProps> = {}) => {
    if (activity) {
      activity.trace(`Showing dialog "${dialogName}".`);
    }

    setAdditionalProps({
      ...periodProps,
      hidden: false,
    });

    if (onDisplay && isFunction(onDisplay)) {
      onDisplay();
    }
  }, [setAdditionalProps, activity, dialogName, onDisplay]);

  const dismissDialog = useCallback((buttonKey: string) => {
    if (activity) {
      activity.trace(`Dialog "${dialogName}" dismissed with button "${buttonKey}".`);
    }

    // Keep additionalProps here to avoid the dialog renders again
    //   without additionalProps before hidding.
    setAdditionalProps({
      ...additionalProps,
      hidden: true,
    });
  }, [additionalProps, setAdditionalProps, activity, dialogName]);

  const internalOnCancel = useCallback(() => {
    dismissDialog('cancel');

    if (originalOnCancel) {
      originalOnCancel();
    }
  }, [dismissDialog, originalOnCancel]);

  const internalOnDismiss = useCallback(() => {
    dismissDialog('close');

    if (originalOnDismiss) {
      originalOnDismiss();
    }
  }, [dismissDialog, originalOnDismiss]);

  const onConfirmClicked = useCallback(() => {
    dismissDialog('confirm');

    onConfirm();
  }, [onConfirm, dismissDialog]);

  return [
    (
      <ConfirmDialog
        {...passThroughProps}
        hidden={hidden}

        onConfirm={onConfirmClicked}

        secondaryButtons={secondaryButtons.map(buttonProps => (<SecondaryButton
          {...buttonProps}
          key={buttonProps.key}
          buttonKey={buttonProps.key}
          dismissDialog={dismissDialog}
        />))}

        onCancel={internalOnCancel}
        onDismiss={originalOnDismiss && internalOnDismiss}
      >
        {content}
      </ConfirmDialog>
    ),
    showDialog,
    !hidden,
  ];
}

interface PromiseDialogStatics {
  showDialog?: ShowDialog;
  promise?: Promise<unknown> | null;
}

export type UseConfirmDialogWithPromiseResult = [
  React.ReactElement,
  (periodProps?: Partial<UseConfirmDialogProps>) => Promise<unknown>,
  boolean,
];

export function useConfirmDialogWithPromise(props: UseConfirmDialogProps): UseConfirmDialogWithPromiseResult {
  const { current: statics } = useRef<PromiseDialogStatics>({});
  const showDialogAdapter = useCallback((_options: unknown, periodProps?: Partial<UseConfirmDialogProps>) => {
    statics.showDialog?.(periodProps);
  }, [statics]);

  const [, {
    createPromise,
    resolveLastCreatedPromise: resolvePromise,
    rejectLastCreatedPromise: rejectPromise,
  }] = useCreatePromise<unknown, [Partial<UseConfirmDialogProps>?]>(showDialogAdapter);

  const [dialog, originalShowDialog, visible] = useConfirmDialog({
    ...props,
    onConfirm: resolvePromise,
    onCancel: rejectPromise,
  });

  useEffect(() => {
    statics.showDialog = originalShowDialog;
  }, [originalShowDialog, statics]);

  const { onConfirm, onCancel } = props;

  const showDialogWithPromise = useCallback((periodProps?: Partial<UseConfirmDialogProps>) => {
    if (statics.promise) {
      return Promise.reject();
    }

    statics.promise = createPromise(periodProps);

    return statics.promise
      .then(
        onConfirm,
        () => {
          if (onCancel) {
            onCancel();
          }

          return Promise.reject();
        }
      )
      .finally(() => {
        statics.promise = null;
      });
  }, [statics, createPromise, onConfirm, onCancel]);

  return [dialog, showDialogWithPromise, visible];
}
